import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { CashCount } from '../../components/nursery/counter/CashCount';
import nurseryCounterService from '../../services/nurseryCounter';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { breakdownTotal, cleanBreakdown, parseAmount, varianceText } from '../../utils/counterCash';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import type { CashBreakdown, TillList, TillSession } from '../../types/nurseryCounter';

/**
 * The tills cash is taken into at the counter. A till is opened with a
 * counted float, takes cash sales and gives change, and is closed with a
 * count; what it should have held and the difference are kept with it.
 * Nothing recorded here is ever changed or deleted.
 */

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function Tills() {
  const { t } = useTranslation('nursery', { keyPrefix: 'Tills' });
  const timezone = useOrgTimezone();
  const navigate = useNavigate();
  const { hasAccess: canWork } = useStockSeat('manage');
  const [list, setList] = useState<TillList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [opening, setOpening] = useState(false);
  const [name, setName] = useState(() => t('defaultName'));
  const [count, setCount] = useState<CashBreakdown>({});
  const [floatTyped, setFloatTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.listTills();
      if (res.success) setList(res.data);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const counted = cleanBreakdown(count);
  const floatValue = counted ? breakdownTotal(counted) : parseAmount(floatTyped);

  const open = async () => {
    if (floatValue == null) return;
    setBusy(true);
    setOpenError(null);
    try {
      const res = await nurseryCounterService.openTill({ till_name: name.trim() || t('defaultName'), opening_float: floatValue, breakdown: counted });
      if (res.success) {
        navigate(`/nursery/tills/${res.data.id}`);
        return;
      }
      setOpenError(errorMessage(res, t('openFailed')));
    } catch (e) {
      setOpenError(errorMessage(e, t('openFailed')));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={canWork && !opening ? (
          <Button size="sm" onClick={() => setOpening(true)}>{t('openATill')}</Button>
        ) : undefined}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {opening && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4" aria-label={t('openATill')}>
            <h2 className="text-base font-semibold text-neutral-900">{t('openATill')}</h2>
            <div>
              <label htmlFor="till-name" className="block text-sm font-medium text-neutral-700 mb-1">{t('till')}</label>
              <input id="till-name" type="text" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className={input} />
              <p className="text-xs text-neutral-500 mt-1">{t('tillHint')}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-700">{t('countFloat')}</p>
              <CashCount idPrefix="float" value={count} onChange={setCount} denominations={list?.denominations} />
              {!counted && (
                <div>
                  <label htmlFor="till-float" className="block text-sm font-medium text-neutral-700 mb-1">{t('typeFloat')}</label>
                  <input id="till-float" type="text" inputMode="decimal" value={floatTyped}
                    onChange={(e) => setFloatTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
                </div>
              )}
              <p className="text-sm text-neutral-800">
                {t('float')} <span className="font-semibold">{floatValue != null ? money(floatValue) : '—'}</span>
              </p>
            </div>
            {openError && <p className="text-sm text-red-700 break-words" role="alert">{openError}</p>}
            <div className="flex flex-wrap gap-2">
              <Button onClick={open} disabled={busy || floatValue == null}>{busy ? t('opening') : t('openTheTill')}</Button>
              <Button variant="outline" onClick={() => setOpening(false)} disabled={busy}>{t('cancel')}</Button>
            </div>
          </section>
        )}

        {list && (
          <>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('openHeading')}</h2>
              {list.open.length === 0 ? (
                <p className="text-sm text-neutral-600 bg-white border border-neutral-200 rounded-xl px-4 py-6 text-center">
                  {t('noneOpen')}
                </p>
              ) : list.open.map((s) => <TillRow key={s.id} session={s} timezone={timezone} />)}
            </section>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('closedHeading')}</h2>
              {list.closed.length === 0
                ? <p className="text-sm text-neutral-500">{t('noneYet')}</p>
                : list.closed.map((s) => <TillRow key={s.id} session={s} timezone={timezone} />)}
            </section>
          </>
        )}
      </div>
    </>
  );
}

function TillRow({ session: s, timezone }: { session: TillSession; timezone: string }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'Tills' });
  const totals = s.totals;
  const out = s.variance != null && Math.abs(s.variance) >= 0.005;
  return (
    <Link to={`/nursery/tills/${s.id}`}
      className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 hover:border-primary-300 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-neutral-900 break-words">
          {s.till_name}
          <span className="font-normal text-neutral-500">{t('opened', { when: formatDateTimeInTz(s.opened_at, timezone) })}{s.opened_by_name ? t('by', { name: s.opened_by_name }) : ''}</span>
        </p>
        <p className="text-sm text-neutral-600 break-words">
          {s.status === 'open'
            ? t('shouldHold', { amount: money(totals?.expected_cash ?? s.opening_float), count: totals?.sale_count ?? 0 })
            : t('countedAgainst', { counted: money(s.counted_cash ?? 0), expected: money(s.expected_cash ?? 0) })}
          {s.status === 'closed' && (
            <span className={out ? 'text-red-700 font-medium' : 'text-green-700'}> · {varianceText(s.variance)}</span>
          )}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
    </Link>
  );
}

export default Tills;
